/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    FONT_COLOR_COMPLIANCE_FIX
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Replaced hardcoded font-family and color values with SCSS variables
 * for design system compliance. (Poppins → var(--font-family-poppins),
 * #212F20 → var(--color-black))
 *
 * Related Screens / Modules:
 * - client/src/components/setup/broadcast-signature-editor/utils/tinymceConfig.ts
 * - client/src/styles/variables/_typography.scss
 * - client/src/styles/variables/_colors.scss
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    COMPLIANCE_FIX_2025_12_30
 * Date:         2025-12-30
 * Modified By:  Cursor
 *
 * Description:
 * Fixed font-family to use Poppins as primary font.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/broadcast-signature-editor/utils/tinymceConfig.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TINYMCE_6_MIGRATION_CONFIG
 * Date:         2025-12-29
 * Modified By:  Cursor
 *
 * Description:
 * Migrated TinyMCE configuration to version 6.0. Removed deprecated plugins 
 * (textcolor, colorpicker) and options (paste_retain_style_properties).
 * Extracted from BroadcastSignatureEditor.tsx to meet line limits.
 *
 * Related Screens / Modules:
 * - client/src/components/setup/broadcast-signature-editor/utils/tinymceConfig.ts
 */

export const getTinyMCEConfig = (placeholder: string) => ({
  height: 300,
  menubar: false,

  // [2025-12-21] Custom setup for image dialog + delete button
  setup: (editor: any) => {
    // [2026-02-06] Auto-open Upload tab and hide General tab in image dialog
    editor.on("OpenWindow", () => {
      setTimeout(() => {
        const tabs = document.querySelectorAll(
          ".tox-dialog [role=\"tab\"]",
        );
        // Hide General tab, click Upload tab
        if (tabs.length >= 2) {
          (tabs[0] as HTMLElement).style.display = "none"; // General
          (tabs[1] as HTMLElement)?.click(); // Click Upload tab
        }
      }, 50);
    });

    // [2025-12-21] Add delete button (X) overlay when an image is selected
    editor.on("NodeChange", (e: any) => {
      const node = e.element as HTMLElement | null;

      // Remove any existing delete button first
      const oldBtn = document.querySelector(
        ".image-delete-btn",
      ) as HTMLButtonElement | null;
      if (oldBtn) {
        oldBtn.remove();
      }

      if (node && node.tagName === "IMG") {
        const deleteBtn = document.createElement("button");
        deleteBtn.className = "image-delete-btn";
        deleteBtn.innerHTML = "×";
        // [2025-01-20] Color compliance: Use design token for delete button (changed from #dc3545)
        deleteBtn.style.cssText = `
          position: absolute;
          top: -10px;
          right: -10px;
          width: 24px;
          height: 24px;
          background: var(--color-status-red);
          color: white;
          border: 2px solid white;
          border-radius: 50%;
          cursor: pointer;
          font-size: 18px;
          line-height: 20px;
          z-index: 1000;
          box-shadow: 0 2px 4px rgba(0,0,0,0.3);
        `;

        deleteBtn.onclick = (clickEvent) => {
          clickEvent.preventDefault();
          clickEvent.stopPropagation();
          editor.dom.remove(node);
          deleteBtn.remove();
        };

        const rect = node.getBoundingClientRect();
        const editorRect =
          editor.getContainer().getBoundingClientRect();

        deleteBtn.style.left = `${
          rect.right - editorRect.left - 10
        }px`;
        deleteBtn.style.top = `${rect.top - editorRect.top - 10}px`;

        editor.getContainer().appendChild(deleteBtn);
      }
    });
  },

  // [2025-12-29] TinyMCE 6 Migration: Removed textcolor and colorpicker plugins (now in core)
  plugins: [
    "lists", // Ordered/bullet lists
    "link", // Link insert/edit
    "image", // Image insert/resize
    "code", // View HTML code (debug / advanced)
  ],

  // [2025-12-21] CRITICAL: Preserve all HTML attributes and styles
  verify_html: false,
  cleanup: false,
  convert_urls: false,
  remove_script_host: false,
  relative_urls: false,
  entity_encoding: "raw" as const,
  inline_styles: true,

  // [2025-12-21] Toolbar layout grouped roughly by rows
  toolbar: [
    "undo redo",
    "blocks fontsize",
    "bold italic underline strikethrough",
    "bullist numlist",
    "forecolor backcolor",
    "link image",
    "removeformat",
  ].join(" | "),

  toolbar_mode: "sliding" as const,

  // [2025-12-21] Block and font size formats
  block_formats:
    "Paragraph=p; Header 1=h1; Header 2=h2; Header 3=h3; Header 4=h4",
  fontsize_formats:
    "8pt 10pt 12pt 14pt 16pt 18pt 20pt 24pt 30pt 36pt 48pt 60pt 72pt",

  // [2025-12-21] Force inline styles for font size
  formats: {
    fontsize: { inline: "span", styles: { "font-size": "%value" } },
  },

  // [2025-12-21] Image configuration – base64 upload + resize
  // [2026-02-06] Disabled advanced tab and title - only upload tab shown
  image_advtab: false,
  image_title: false,
  automatic_uploads: true,
  file_picker_types: "image",

  // [2025-12-21] Image upload handler with validation and error handling.
  images_upload_handler: (blobInfo: any) => {
    return new Promise<string>((resolve, reject) => {
      try {
        const blob = blobInfo.blob();

        // [2025-12-21] Validate file size (max 5MB for email signatures)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (blob.size > maxSize) {
          reject("Image is too large. Maximum size is 5MB.");
          return;
        }

        // [2025-12-21] Validate file type
        const validTypes = [
          "image/jpeg",
          "image/png",
          "image/gif",
          "image/webp",
        ];
        if (!validTypes.includes(blob.type)) {
          reject(
            "Invalid image type. Please use JPEG, PNG, GIF, or WebP.",
          );
          return;
        }

        const reader = new FileReader();

        reader.onload = () => {
          const base64 = reader.result as string;

          // [2025-12-21] Validate base64 result
          if (!base64 || !base64.startsWith("data:image")) {
            reject("Failed to read image data");
            return;
          }

          // [2025-12-21] Resolve Promise with base64 string
          resolve(base64);
        };

        reader.onerror = (error) => {
          // eslint-disable-next-line no-console
          console.error("FileReader error:", error);
          reject("Failed to read image file. Please try again.");
        };

        // [2025-12-21] Convert file to base64 data URL for inline embedding
        reader.readAsDataURL(blob);
      } catch (error: any) {
        // eslint-disable-next-line no-console
        console.error("Image upload error:", error);
        reject(
          `Image upload error: ${error?.message ?? "Unknown error"}`,
        );
      }
    });
  },

  // [2025-12-21] Enable image resize with handles
  image_dimensions: true,
  object_resizing: true,
  resize_img_proportional: false,

  // [2025-12-21] Default content style (rough match to previous Quill)
  content_style: `
    body {
      font-family: var(--font-family-poppins);
      // [2025-12-30] Font compliance: Using SCSS variable
      font-size: 14px;
      line-height: 1.5;
      color: var(--color-black);
      // [2025-12-30] Color compliance: Using SCSS variable
      padding: 10px;
    }
    h1 { font-size: 2em; font-weight: bold; margin: 0.67em 0; }
    h2 { font-size: 1.5em; font-weight: bold; margin: 0.75em 0; }
    h3 { font-size: 1.17em; font-weight: bold; margin: 0.83em 0; }
    h4 { font-size: 1em; font-weight: bold; margin: 1.12em 0; }
    p { margin: 1em 0; }
    img { max-width: 100%; height: auto; }
  `,

  // [2025-12-21] Placeholder support
  placeholder: placeholder || "Enter your signature here...",

  // [2025-12-21] Simple color palette (can be tuned to match Quill)
  color_map: [
    "000000", "Black",
    "808080", "Gray",
    "FFFFFF", "White",
    "FF0000", "Red",
    "FFFF00", "Yellow",
    "00FF00", "Green",
    "00FFFF", "Cyan",
    "0000FF", "Blue",
    "FF00FF", "Magenta",
  ],

  // [2025-12-21] Allow ALL elements and styles - no filtering
  valid_elements: "*[*]",
  valid_children: "+body[style],+p[span]",
  extended_valid_elements: "*[*]",
  valid_styles: {
    "*":
      "font-size,color,background-color,font-weight,font-style,text-decoration,text-align,margin,padding,border,width,height",
  },
  invalid_elements: "",
  invalid_styles: "",

  paste_as_text: false,
  // [2025-12-29] TinyMCE 6 Migration: Removed paste_retain_style_properties (deprecated)

  browser_spellcheck: true,
  branding: false,
  statusbar: true,
  elementpath: true,
  resize: true,
});
